import { isRight } from 'fp-ts/Either';
import { useEffect, useState } from 'react';
import type { TRPC } from '../../../contexts/TRPC';
import type { RouterOutputs, RightResult } from '../../../types/TRPC';
import type { UserRole } from '../../../utils/roleUtils';
import { captureAndReportError } from '../../../utils/errorReporting';

type RuntimeSettings = RightResult<
  RouterOutputs['getHubspotTicketModeratorRuntimeSettings']
>;

export interface ModeratorCapabilities {
  canReviewNewApps: boolean;
  canReviewUpdates: boolean;
  canApprove: boolean;
  canRejectDirectly: boolean;
  canReassignTickets: boolean;
  canViewAuditTrail: boolean;
  requiresReviewChecklist: boolean;
}

export interface UseModeratorCapabilitiesResult {
  capabilities: ModeratorCapabilities | null;
  presetName: string | null;
  rejectionFailureAssigneeUserId: string | null;
  isLoading: boolean;
}

const DEFAULT_MODERATOR_CAPABILITIES: ModeratorCapabilities = {
  canReviewNewApps: false,
  canReviewUpdates: true,
  canApprove: true,
  canRejectDirectly: false,
  canReassignTickets: false,
  canViewAuditTrail: false,
  requiresReviewChecklist: true,
};

export function useModeratorCapabilities({
  viewerRole,
  trpc,
}: {
  viewerRole: UserRole;
  trpc: TRPC | null;
}): UseModeratorCapabilitiesResult {
  const [result, setResult] = useState<UseModeratorCapabilitiesResult>({
    capabilities: null,
    presetName: null,
    rejectionFailureAssigneeUserId: null,
    isLoading: viewerRole === 'moderator',
  });

  useEffect(() => {
    if (viewerRole !== 'moderator' || !trpc?.client) {
      setResult({
        capabilities: null,
        presetName: null,
        rejectionFailureAssigneeUserId: null,
        isLoading: false,
      });
      return;
    }

    let cancelled = false;

    const fetch = async () => {
      try {
        const response =
          await trpc.client.getHubspotTicketModeratorRuntimeSettings.query(
            undefined,
          );

        if (cancelled) return;

        if (isRight(response)) {
          const settings: RuntimeSettings = response.right;
          setResult({
            capabilities:
              settings.capabilities ?? DEFAULT_MODERATOR_CAPABILITIES,
            presetName: settings.presetName,
            rejectionFailureAssigneeUserId:
              settings.rejectionFailureAssigneeUserId,
            isLoading: false,
          });
        } else {
          setResult({
            capabilities: DEFAULT_MODERATOR_CAPABILITIES,
            presetName: null,
            rejectionFailureAssigneeUserId: null,
            isLoading: false,
          });
        }
      } catch (err) {
        captureAndReportError('Error fetching moderator capabilities:', err);
        if (!cancelled) {
          setResult({
            capabilities: DEFAULT_MODERATOR_CAPABILITIES,
            presetName: null,
            rejectionFailureAssigneeUserId: null,
            isLoading: false,
          });
        }
      }
    };

    void fetch();

    return () => {
      cancelled = true;
    };
  }, [viewerRole, trpc]);

  return result;
}
